import { supabase } from '../lib/supabase';

export const statementImportsService = {
  // Get all statement imports for the organization
  async getAll(organizationId) {
    const { data, error } = await supabase?.from('statement_imports')?.select(`
        *,
        bank_accounts!inner(id, account_name, bank_name),
        user_profiles!created_by(id, full_name, email)
      `)?.eq('organization_id', organizationId)?.order('created_at', { ascending: false });

    if (error) throw error;

    // Convert snake_case to camelCase
    return data?.map(row => ({
      id: row?.id,
      organizationId: row?.organization_id,
      bankAccountId: row?.bank_account_id,
      createdBy: row?.created_by,
      fileName: row?.file_name,
      filePath: row?.file_path,
      fileType: row?.file_type,
      fileSize: row?.file_size,
      status: row?.status,
      totalTransactions: row?.total_transactions,
      importedTransactions: row?.imported_transactions,
      failedTransactions: row?.failed_transactions,
      startDate: row?.start_date,
      endDate: row?.end_date,
      errorMessage: row?.error_message,
      processingStartedAt: row?.processing_started_at,
      processingCompletedAt: row?.processing_completed_at,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at,
      bankAccount: {
        id: row?.bank_accounts?.id,
        accountName: row?.bank_accounts?.account_name,
        bankName: row?.bank_accounts?.bank_name
      },
      creator: {
        id: row?.user_profiles?.id,
        fullName: row?.user_profiles?.full_name,
        email: row?.user_profiles?.email
      }
    }));
  },

  // Create new statement import
  async create(importData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('statement_imports')?.insert({
        organization_id: importData?.organizationId,
        bank_account_id: importData?.bankAccountId,
        created_by: user?.id,
        file_name: importData?.fileName,
        file_path: importData?.filePath,
        file_type: importData?.fileType,
        file_size: importData?.fileSize,
        status: 'pending'
      })?.select()?.single();

    if (error) throw error;

    // Convert back to camelCase
    return {
      id: data?.id,
      organizationId: data?.organization_id,
      bankAccountId: data?.bank_account_id,
      createdBy: data?.created_by,
      fileName: data?.file_name,
      filePath: data?.file_path,
      fileType: data?.file_type,
      fileSize: data?.file_size,
      status: data?.status,
      createdAt: data?.created_at
    };
  },

  // Upload statement file
  async uploadFile(file, organizationId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const timestamp = Date.now();
    const filePath = `${organizationId}/statements/${timestamp}-${file?.name}`;

    const { data, error } = await supabase?.storage?.from('statement-imports')?.upload(filePath, file);

    if (error) throw error;

    return {
      path: data?.path,
      fullPath: data?.fullPath,
      fileName: file?.name,
      fileSize: file?.size
    };
  },

  // Get signed URL for file download - Enhanced with robust error handling
  async getFileUrl(filePath) {
    try {
      // Validate input
      if (!filePath) {
        throw new Error('File path is required');
      }

      // Directly create signed URL - simpler and more reliable approach
      const { data, error } = await supabase
        ?.storage
        ?.from('statement-imports')
        ?.createSignedUrl(filePath, 3600); // 1 hour expiry

      if (error) {
        // Handle specific Supabase storage errors
        if (error?.message?.includes('Object not found') || 
            error?.message?.includes('not found') ||
            error?.statusCode === 404) {
          throw new Error('File not found in storage. It may have been deleted or the upload was incomplete.');
        } else if (error?.message?.includes('Invalid key') || 
                   error?.message?.includes('Key not found')) {
          throw new Error('File not found in storage. The file path may be invalid or the file was removed.');
        } else if (error?.message?.includes('access denied') || 
                   error?.message?.includes('permission')) {
          throw new Error('Access denied. You may not have permission to download this file.');
        } else {
          throw new Error(`Storage error: ${error?.message}`);
        }
      }

      if (!data?.signedUrl) {
        throw new Error('Failed to generate download URL. Please try again.');
      }

      return data?.signedUrl;
    } catch (error) {
      // Re-throw with more context for better user experience
      throw new Error(`Failed to generate download link: ${error?.message}`);
    }
  },

  // Update import status
  async updateStatus(importId, status, processingData = {}) {
    const updateData = {
      status,
      updated_at: new Date()?.toISOString()
    };

    // Add processing data if provided
    if (processingData?.totalTransactions !== undefined) {
      updateData.total_transactions = processingData?.totalTransactions;
    }
    if (processingData?.importedTransactions !== undefined) {
      updateData.imported_transactions = processingData?.importedTransactions;
    }
    if (processingData?.failedTransactions !== undefined) {
      updateData.failed_transactions = processingData?.failedTransactions;
    }
    if (processingData?.errorMessage !== undefined) {
      updateData.error_message = processingData?.errorMessage;
    }
    if (processingData?.startDate !== undefined) {
      updateData.start_date = processingData?.startDate;
    }
    if (processingData?.endDate !== undefined) {
      updateData.end_date = processingData?.endDate;
    }

    // Set processing timestamps
    if (status === 'processing' && !processingData?.processingStartedAt) {
      updateData.processing_started_at = new Date()?.toISOString();
    }
    if (['completed', 'failed']?.includes(status) && !processingData?.processingCompletedAt) {
      updateData.processing_completed_at = new Date()?.toISOString();
    }

    const { data, error } = await supabase?.from('statement_imports')?.update(updateData)?.eq('id', importId)?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      status: data?.status,
      updatedAt: data?.updated_at
    };
  },

  // Delete import
  async delete(importId) {
    // First, get the file path to delete from storage
    const { data: importRecord } = await supabase?.from('statement_imports')?.select('file_path')?.eq('id', importId)?.single();

    if (importRecord?.file_path) {
      // Delete file from storage (ignore errors if file doesn't exist)
      try {
        await supabase?.storage?.from('statement-imports')?.remove([importRecord?.file_path]);
      } catch (storageError) {
        // Log but don't fail the operation if file deletion fails
        console.warn('Failed to delete file from storage:', storageError?.message);
      }
    }

    // Delete import record (cascade will handle errors table)
    const { error } = await supabase?.from('statement_imports')?.delete()?.eq('id', importId);

    if (error) throw error;
  },

  // Get import errors
  async getErrors(importId) {
    const { data, error } = await supabase?.from('statement_import_errors')?.select('*')?.eq('statement_import_id', importId)?.order('row_number', { ascending: true });

    if (error) throw error;

    return data?.map(row => ({
      id: row?.id,
      statementImportId: row?.statement_import_id,
      rowNumber: row?.row_number,
      errorType: row?.error_type,
      errorMessage: row?.error_message,
      rawData: row?.raw_data,
      createdAt: row?.created_at
    }));
  },

  // Get processing statistics
  async getStats(organizationId) {
    const { data, error } = await supabase?.from('statement_imports')?.select('status, created_at')?.eq('organization_id', organizationId)?.gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)?.toISOString()); // Last 30 days

    if (error) throw error;

    const stats = {
      total: data?.length,
      pending: data?.filter(i => i?.status === 'pending')?.length,
      processing: data?.filter(i => i?.status === 'processing')?.length,
      completed: data?.filter(i => i?.status === 'completed')?.length,
      failed: data?.filter(i => i?.status === 'failed')?.length,
      cancelled: data?.filter(i => i?.status === 'cancelled')?.length
    };

    return stats;
  }
};